import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  TranslationService
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { UigB2BUser } from 'src/app/organization/uig-user/uig-user.model';
import { CustomVendorReportingUtils } from '../../custom-vendor-reporting-utils';
import {
  CustomVendorReportingSearchFilters,
  CustomVendorReportingSortOptions,
  ReportCategory,
  ReportTransferStatus,
  ReportTransferType,
  CustomRioDataEvaluationOverviewList,
  RioDataEvaluationData,
  RioStatisticsData,
  RioDataEvaluationSortOptions,
} from '../../custom-vendor-reporting.models';
import { CustomVendorReportingService } from '../../custom-vendor-reporting.service';
import {
  clearVendorReportingOverview,
  clearVendorReportingSearchFilters,
  getRioDataEvaluationOverview,
} from '../../store/custom-vendor-reporting.action';
import { CustomVendorReportingState } from '../../store/custom-vendor-reporting.reducer';
import {
  selectVendorReportingFilters,
  selectRioDataEvaluationOverview,
  errorRioDataEvaluationOverview,
  selectRioDataEvaluationFilters,
} from '../../store/custom-vendor-reporting.selector';

@Component({
    selector: 'app-custom-vendor-report-data-evaluation',
    templateUrl: './custom-vendor-report-data-evaluation.component.html',
    styleUrls: ['./custom-vendor-report-data-evaluation.component.scss', './custom-vendor-report-data-evaluation-print.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomVendorReportDataEvaluationComponent  implements OnInit, OnDestroy {
  @ViewChild('searchForm') searchForm: NgForm;
  reportTransferStatusEnum = ReportTransferStatus;

  subscription = new Subscription();

  rioDataEvaluationOverview$: Observable<CustomRioDataEvaluationOverviewList> =
    this.store.select(selectRioDataEvaluationOverview);

  errorOverview$: Observable<Error> = this.store.select(
    errorRioDataEvaluationOverview
  );

  searchFilters$: Observable<CustomVendorReportingSearchFilters> = this.store
    .select(selectRioDataEvaluationFilters)
    .pipe(
      tap((filters) => {
        this.filters = { ...filters };
        this.setSearchFiltersDates(this.filters);
      })
    );

  manufacturersList$ = new  Observable<String[]>()

  rioDataEvaluationOverview: CustomRioDataEvaluationOverviewList;
  errorOverview: Error;

  vendorReportTransferDataList: RioDataEvaluationData[];
  rioEvaluationStatisticsData:RioStatisticsData;

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'desc';

  showSuccessfullyTransferred: boolean;
  filterSuccessfullyTransferred$ = new BehaviorSubject<boolean>(false);
  isFilteringrSuccessfullyTransferred$ = new BehaviorSubject<boolean>(false);

  filters: CustomVendorReportingSearchFilters;

  fileName: string = '';

  currentUserB2BUnit: string;
  reportTransferTypesList = Object.values(ReportTransferType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  displayVendorReportDashboardProperties: boolean;

  constructor(
    private store: Store<CustomVendorReportingState>,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private datePipe: DatePipe,
    private vendorReportingService: CustomVendorReportingService,
    private userService: UserAccountFacade
  ) {}

  ngOnInit(): void {
    this.setSortOptions();
    this.subscription.add(
    this.userService
        .get()
        .pipe(
          filter((user: UigB2BUser) => user?.orgUnit !== undefined),
          map((user) => user?.orgUnit?.uid)
        )
        .subscribe((unitId) => {
          this.currentUserB2BUnit = unitId;
          this.getRioDataEvaluationOverview(this.filters);
        })
    );

    combineLatest([
      this.rioDataEvaluationOverview$,
      this.filterSuccessfullyTransferred$,
    ])
      .pipe(
        filter(([vendorReportingOverview]) => vendorReportingOverview != null),
        tap(([vendorReportingOverview, ]) => {
          this.rioDataEvaluationOverview = vendorReportingOverview;
          this.vendorReportTransferDataList = [
            ...vendorReportingOverview.responses,
          ];
          this.rioEvaluationStatisticsData = this.rioDataEvaluationOverview.statistics;
          this.filterSuccessfullyTransferred();
          this.loadManufacturersDropdown();
          this.onChangeSort();
          this.isFilteringrSuccessfullyTransferred$.next(false);
        })
      )
      .subscribe();

    this.errorOverview$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorOverview = error;
        }),
        switchMap(() =>
          this.translation.translate('customVendorReporting.error')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
  }

  loadManufacturersDropdown() {
    this.manufacturersList$ = this.vendorReportingService.getManufacturerMappings()
    .pipe(map(manufactuersList => {
      const manufacturers = manufactuersList.map(m => m.manufacturer);
      return manufacturers.filter(m => this.vendorReportTransferDataList.map(transferData => transferData.manufacturer).includes(m));
    }));
  }

  setSearchFiltersDates(filters: CustomVendorReportingSearchFilters) {
    filters.transferDateFrom =
      filters.transferDateFrom === ''
        ? this.datePipe.transform(
            CustomVendorReportingUtils.getDefaultRioTransferDateFrom(new Date()),
            CustomVendorReportingUtils.DATE_FIELDS_FORMAT
          )
        : filters.transferDateFrom;
    filters.transferDateTo =
      filters.transferDateTo === ''
        ? this.datePipe.transform(
            new Date(),
            CustomVendorReportingUtils.DATE_FIELDS_FORMAT
          )
        : filters.transferDateTo;
  }

  filterSuccessfullyTransferred(): void {
    if (this.showSuccessfullyTransferred) {
      this.vendorReportTransferDataList = [
        ...this.rioDataEvaluationOverview.responses,
      ];
      this.rioEvaluationStatisticsData=this.rioDataEvaluationOverview.statistics;
      return;
    }
  }

  fireFilterSuccessfullyTransferred(): void {
    this.isFilteringrSuccessfullyTransferred$.next(true);
    this.filterSuccessfullyTransferred$.next(this.showSuccessfullyTransferred);
  }

  setSortOptions(): void {
    this.selectedSortOption =RioDataEvaluationSortOptions[RioDataEvaluationSortOptions.date];
    this.sortOptions = Object.values(RioDataEvaluationSortOptions).filter(
      (value) => typeof value === 'string'
    ) as string[];
  }

  getRioDataEvaluationOverview(
    filters: CustomVendorReportingSearchFilters = CustomVendorReportingUtils.initDataEvaluationSearchFilters()
  ): void {
    this.rioDataEvaluationOverview = null;
    this.vendorReportTransferDataList = null;
    this.errorOverview = null;
    this.setSearchFiltersDates(filters);
    this.store.dispatch(getRioDataEvaluationOverview({ searchFilters: filters }));
  }

  onSubmit(): void {
    this.getRioDataEvaluationOverview(this.filters);
  }

  resetForm(): void {
    this.sortDirection = 'desc';
    this.setSortOptions();
    this.getRioDataEvaluationOverview();
  }

  onChangeSort(): void {
    this.vendorReportTransferDataList = this.vendorReportTransferDataList.sort(
      (a, b) => {
        const res = CustomVendorReportingUtils.compare(
          this.selectedSortOption,
          a[this.selectedSortOption],
          b[this.selectedSortOption]
        );
        return this.sortDirection === 'asc' ? res : -res;
      }
    );
  }

  onReportTransferTypeChange() {
    this.selectedSortOption =
    RioDataEvaluationSortOptions[
     RioDataEvaluationSortOptions.date
   ];
    this.sortDirection = 'desc';
  }

  validateTransferDateFrom(): void {
    if (
      new Date(this.filters.transferDateFrom) >
      new Date(this.filters.transferDateTo)
    ) {
      this.filters.transferDateTo = this.filters.transferDateFrom;
    }
  }

  validateTransferDateTo(): void {
    if (
      new Date(this.filters.transferDateTo) <
      new Date(this.filters.transferDateFrom)
    ) {
      this.filters.transferDateFrom = this.filters.transferDateTo;
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearVendorReportingOverview());
    this.store.dispatch(clearVendorReportingSearchFilters());
    this.subscription.unsubscribe();
  }

  onExport() {
    this.vendorReportingService.exportRioData(this.filters);
 }
}
